import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type {
  ConversationSnoozeOptions,
  ConversationStatus,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useHandler } from "@redotech/react-util/hook";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { assertNever } from "@redotech/util/type";
import { useMemo, useState } from "react";
import {
  useArchiveConversationAction,
  useCloseConversationAction,
  useMarkInProgressConversationAction,
  useReopenConversationAction,
  useSnoozeConversationAction,
} from "../actions/use-conversation-actions";

export type StatusOption =
  | { type: "status"; status: ConversationStatus }
  | { type: "snooze"; snooze: ConversationSnoozeOptions };

export interface UseStatusSelectorResult {
  statusListItems: RedoListItem<StatusOption>[];
  dropdownOpen: boolean;
  badgeRef: HTMLElement | null;
  setBadgeRef: (ref: HTMLElement | null) => void;
  handleBadgeClick: () => void;
  handleDropdownOpenChange: (open: boolean) => void;
  handleItemSelected: (option: StatusOption) => void;
}

const SNOOZE_CHILDREN: RedoListItem<StatusOption>[] = [
  {
    type: "text",
    id: "snooze-1h",
    text: "1 hour",
    value: { type: "snooze", snooze: "1h" },
  },
  {
    type: "text",
    id: "snooze-4h",
    text: "4 hours",
    value: { type: "snooze", snooze: "4h" },
  },
  {
    type: "text",
    id: "snooze-24h",
    text: "24 hours",
    value: { type: "snooze", snooze: "24h" },
  },
  {
    type: "text",
    id: "snooze-custom",
    text: "Custom...",
    value: { type: "snooze", snooze: "custom" },
  },
];

const STATUS_LIST_ITEMS: RedoListItem<StatusOption>[] = [
  {
    type: "text",
    id: "status-open",
    text: "Open",
    value: { type: "status", status: "OPEN" },
  },
  {
    type: "text",
    id: "status-closed",
    text: "Closed",
    value: { type: "status", status: "CLOSED" },
  },
  {
    type: "text",
    id: "status-in-progress",
    text: "In Progress",
    value: { type: "status", status: "IN_PROGRESS" },
  },
  {
    type: "parent",
    id: "status-snoozed",
    text: "Snoozed",
    children: SNOOZE_CHILDREN,
    value: { type: "status", status: "SNOOZED" },
  },
  {
    type: "text",
    id: "status-archived",
    text: "Archived",
    value: { type: "status", status: "ARCHIVED" },
  },
];

export function useStatusSelector({
  conversationId,
}: {
  conversationId: ConversationId;
}): UseStatusSelectorResult {
  const closeAction = useCloseConversationAction();
  const reopenAction = useReopenConversationAction();
  const archiveAction = useArchiveConversationAction();
  const inProgressAction = useMarkInProgressConversationAction();
  const snoozeAction = useSnoozeConversationAction();

  const [badgeRef, setBadgeRef] = useState<HTMLElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleBadgeClick = useHandler(() => {
    setDropdownOpen((open) => !open);
  });

  const handleDropdownOpenChange = useHandler((open: boolean) => {
    setDropdownOpen(open);
  });

  const handleItemSelected = useHandler((option: StatusOption) => {
    if (option.type === "snooze") {
      if (option.snooze === "custom") {
        // TODO: open custom snooze date picker
        return;
      }
      void snoozeAction.execute(conversationId, { duration: option.snooze });
    } else {
      switch (option.status) {
        case "OPEN":
          void reopenAction.execute(conversationId);
          break;
        case "CLOSED":
          void closeAction.execute(conversationId);
          break;
        case "IN_PROGRESS":
          void inProgressAction.execute(conversationId);
          break;
        case "ARCHIVED":
          void archiveAction.execute(conversationId);
          break;
        case "SNOOZED":
          // Parent item click — sub-items handle specific snooze options
          break;
        case "HIDDEN":
        case "DELETED":
          // Not user-selectable; no action
          break;
        default:
          assertNever(option.status);
      }
    }
    setDropdownOpen(false);
  });

  const statusListItems = useMemo(() => STATUS_LIST_ITEMS, []);

  return {
    statusListItems,
    dropdownOpen,
    badgeRef,
    setBadgeRef,
    handleBadgeClick,
    handleDropdownOpenChange,
    handleItemSelected,
  };
}
